import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/api/queryKeys';
import { answerDesk, askDesk, fetchDeskQuestion, fetchDeskQuestions, type DeskParams } from '../api/deskApi';

const WAITING_POLL_MS = 15_000; // an analyst's reply is minutes away, not seconds

const BADGE_POLL_MS = 60_000;

/** How many questions wait for an analyst: the number beside Desk questions in the menu. */
export function useWaitingDeskCount(enabled: boolean) {
  return useDeskCount('open', enabled);
}

/** How many questions have a status, for the Waiting and Answered tabs. */
export function useDeskCount(status: 'open' | 'answered', enabled = true) {
  const params = { page: 1, pageSize: 1, status };
  return useQuery({
    queryKey: queryKeys.desk.list(params),
    queryFn: ({ signal }) => fetchDeskQuestions(params, signal),
    enabled,
    refetchInterval: BADGE_POLL_MS,
    select: (p) => p.total,
  });
}

export function useDeskQuestionsQuery(params: DeskParams) {
  return useQuery({
    queryKey: queryKeys.desk.list(params),
    queryFn: ({ signal }) => fetchDeskQuestions(params, signal),
    placeholderData: keepPreviousData,
  });
}

/** The asker's view of one question: checks back until an analyst has replied. */
export function useDeskQuestionQuery(id: string | null) {
  return useQuery({
    queryKey: queryKeys.desk.one(id ?? ''),
    queryFn: ({ signal }) => fetchDeskQuestion(id ?? '', signal),
    enabled: id !== null,
    refetchInterval: (q) => (q.state.data?.status === 'answered' ? false : WAITING_POLL_MS),
  });
}

export function useAskDesk() {
  return useMutation({ mutationFn: ({ question, context }: { question: string; context: string[] }) => askDesk(question, context) });
}

export function useAnswerDesk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => answerDesk(id, text),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.desk.all }); },
  });
}
